import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { api } from '@/lib/api'
import { qk } from '@/lib/queryClient'
import type { ServiceHealth } from '@/lib/types'

const STATUS_POLL_MS = 60_000

export interface StatusSummary {
  services: ServiceHealth[]
  down: ServiceHealth[]
  isDegraded: boolean
  /** Newest heartbeat across all services. */
  checkedAt: string | null
  isLoading: boolean
  error: unknown
  refetch: () => void
}

/**
 * `/api/status` is read from a KV heartbeat, never from the home server, so it
 * answers fast even during an outage. It is the source of truth for the banner.
 */
export function useStatus(): StatusSummary {
  const query = useQuery({
    queryKey: qk.status,
    queryFn: ({ signal }) => api.status.get(signal),
    refetchInterval: STATUS_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
    retry: 1,
  })

  const services = useMemo(() => query.data ?? [], [query.data])

  return useMemo(() => {
    const down = services.filter((service) => !service.ok)
    const checkedAt = services.reduce<string | null>((latest, service) => {
      if (!service.checkedAt) return latest
      if (!latest) return service.checkedAt
      return new Date(service.checkedAt) > new Date(latest) ? service.checkedAt : latest
    }, null)

    return {
      services,
      down,
      isDegraded: down.length > 0,
      checkedAt,
      isLoading: query.isPending,
      error: query.error,
      refetch: () => void query.refetch(),
    }
  }, [services, query])
}
